// Student Editor - PartDPage1 (Exact match to teacher panel design - readonly teacher + editable student)
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartDPage1 = () => {
  const { activity } = useActivityContext();
  const [formData, setFormData] = useState({
    prior_preparation: '',
    materials_needed: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const saveTimeoutRef = useRef(null);
  const teacherFields = activity?.part_d?.page_1?.teacher_fields || {};

  useEffect(() => {
    if (activity?.my_response_part_d?.page_1) {
      const page1Data = activity.my_response_part_d.page_1;
      setFormData({
        prior_preparation: page1Data.prior_preparation || '',
        materials_needed: page1Data.materials_needed || ''
      });
    }
  }, [activity]);

  const saveChanges = async (dataToSave) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl} /secondary/user / domain - activities / ${activity.activity_id} /part-d/page / 1 / student - fields`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token} ` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePriorPreparationChange = (value) => {
    setFormData({ ...formData, prior_preparation: value });

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges({ prior_preparation: value }), 1000);
  };

  const handleMaterialsNeededChange = (value) => {
    setFormData({ ...formData, materials_needed: value });

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges({ materials_needed: value }), 1000);
  };

  const hasTeacherData = Object.keys(teacherFields).length > 0;

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '30px',
      backgroundColor: 'white',
      fontFamily: "'Poppins', Arial, sans-serif"  // Updated to Poppins
    }}>
      {/* Save indicator */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saving ? '#FFC107' : '#4CAF50',
          color: saving ? 'black' : 'white',
          padding: '12px 24px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '16px',
          fontWeight: 'bold'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F4824C', marginBottom: '8px' }}>
          PART D
        </div>
      </div>
      <div style={{
        fontSize: '18px',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: '#F4824C',
        color: 'white',
        padding: '15px',
        marginBottom: '25px',
        borderRadius: '4px'
      }}>
        Observation Template for Classroom Interactions
      </div>

      {/* Teacher's Setup - Read Only, exact design match */}
      {!hasTeacherData ? (
        <div style={{
          backgroundColor: '#FFF3E0',
          border: '2px dashed #F4824C',
          padding: '20px',
          marginBottom: '30px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666',
          borderRadius: '4px'
        }}>
          Your teacher hasn't set up this activity yet. Please check back later.
        </div>
      ) : (
        <>
          {/* Interaction Types Section - Readonly */}
          <div style={{
            backgroundColor: '#FFE5D5',
            padding: '20px',
            marginBottom: '20px',
            borderRadius: '4px'
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '15px' }}>
              Tick (✓) the type of classroom interaction.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '13px' }}>
              {teacherFields.interaction_types?.classroom_discussion && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <span>Classroom discussion</span>
                </div>
              )}
              {teacherFields.interaction_types?.organised_debate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <span>Organised debate</span>
                </div>
              )}
              {teacherFields.interaction_types?.simulation_role_play && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <span>Simulation/role play</span>
                </div>
              )}
              {teacherFields.interaction_types?.lab_experiment && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <span>Lab experiment</span>
                </div>
              )}
              {teacherFields.interaction_types?.digital_learning && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <span>Digital learning</span>
                </div>
              )}
              {teacherFields.interaction_types?.custom && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #666',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}>✓</div>
                  <div style={{
                    flex: 1,
                    padding: '8px',
                    fontSize: '13px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}>
                    {teacherFields.interaction_types.custom}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subject(s) Section - Readonly */}
          {teacherFields.subjects && teacherFields.subjects.length > 0 && (
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '200px'
              }}>
                <div>Subject(s)</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal' }}>(Can be more than one)</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: '#FFE5D5',
                  padding: '15px',
                  minHeight: '60px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  borderRadius: '4px'
                }}>
                  {teacherFields.subjects.map((subject, index) => (
                    <span key={index} style={{
                      backgroundColor: 'white',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Curricular goal(s) Section - Readonly */}
          {teacherFields.curricular_goals && teacherFields.curricular_goals.length > 0 && (
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '200px'
              }}>
                <div>Curricular goal(s)</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal' }}>(Can be more than one)</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: '#FFE5D5',
                  padding: '15px',
                  minHeight: '60px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  borderRadius: '4px'
                }}>
                  {teacherFields.curricular_goals.map((goal, index) => (
                    <span key={index} style={{
                      backgroundColor: 'white',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {goal}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Competency(-ies) Section - Readonly */}
          {teacherFields.competencies && teacherFields.competencies.length > 0 && (
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '200px'
              }}>
                <div>Competency(-ies)</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal' }}>(Can be more than one)</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: '#FFE5D5',
                  padding: '15px',
                  minHeight: '60px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  borderRadius: '4px'
                }}>
                  {teacherFields.competencies.map((competency, index) => (
                    <span key={index} style={{
                      backgroundColor: 'white',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {competency}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Pedagogies Section - Readonly */}
          {teacherFields.pedagogies && (
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '20px 25px',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '200px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: '4px'
              }}>
                <div>Pedagogies</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal' }}>(Tick (✓) all that apply.)</div>
              </div>
              <div style={{
                flex: 1,
                backgroundColor: '#FFE5D5',
                padding: '20px',
                borderRadius: '4px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                  {teacherFields.pedagogies.art_integrated && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Art-integrated</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.sports_integrated && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Sports-integrated</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.toy_based && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Toy-based</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.technology_integrated && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Technology-integrated</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.skill_based_learning && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Skill-based learning</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.drama_theatre_integrated && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Drama/Theatre-integrated</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.indian_knowledge_systems && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', gridColumn: '1 / -1' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <span>Indian Knowledge Systems approaches</span>
                    </div>
                  )}
                  {teacherFields.pedagogies.any_other && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', gridColumn: '1 / -1' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #666',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}>✓</div>
                      <input
                        type="text"
                        value={teacherFields.pedagogies.any_other}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '13px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Topic/Theme Section with Duration - Readonly */}
          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', marginBottom: '25px' }}>
            <div style={{ flex: 1 }}>
              <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '12px 25px',
                borderRadius: '25px',
                fontSize: '13px',
                fontWeight: 'bold',
                textAlign: 'center',
                marginBottom: '12px',
                lineHeight: '1.4'
              }}>
                Topic/theme/prompt/question/problem/challenge/planned final output
              </div>
              <div style={{
                backgroundColor: '#FFE5D5',
                padding: '15px',
                fontSize: '14px',
                minHeight: '100px',
                borderRadius: '4px',
                lineHeight: '1.5',
                fontFamily: 'Arial, sans-serif'
              }}>
                {teacherFields.topic_theme || ''}
              </div>
            </div>
            {teacherFields.duration && (
              <div style={{ width: '180px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  Duration
                </div>
                <div style={{
                  backgroundColor: '#FFE5D5',
                  padding: '15px',
                  fontSize: '14px',
                  textAlign: 'center',
                  minHeight: '100px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  {teacherFields.duration}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Student Response Section Title */}
      <div style={{
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#5B9BD5',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '3px solid #5B9BD5'
      }}>
        Your Response (Fill the sections below)
      </div>

      {/* Blue Boxes - Editable */}
      <div style={{ display: 'flex', gap: '25px', alignItems: 'stretch' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            backgroundColor: '#5B9BD5',
            color: 'white',
            padding: '15px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRadius: '4px 4px 0 0'
          }}>
            Prior Preparation / Planning / Review / Recap
          </div>
          <textarea
            value={formData.prior_preparation}
            onChange={(e) => handlePriorPreparationChange(e.target.value)}
            placeholder="Enter your prior preparation, planning, review, or recap notes here..."
            maxLength={2000}
            style={{
              width: '100%',
              backgroundColor: '#BDD7EE',
              padding: '20px',
              fontSize: '14px',
              minHeight: '200px',
              border: '2px solid #5B9BD5',
              borderTop: 'none',
              fontFamily: 'Arial, sans-serif',
              resize: 'vertical',
              lineHeight: '1.6',
              borderRadius: '0 0 4px 4px'
            }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            backgroundColor: '#5B9BD5',
            color: 'white',
            padding: '15px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRadius: '4px 4px 0 0'
          }}>
            Materials needed (if applicable)
          </div>
          <textarea
            value={formData.materials_needed}
            onChange={(e) => handleMaterialsNeededChange(e.target.value)}
            placeholder="List any materials you need for this activity..."
            maxLength={2000}
            style={{
              width: '100%',
              backgroundColor: '#BDD7EE',
              padding: '20px',
              fontSize: '14px',
              minHeight: '200px',
              border: '2px solid #5B9BD5',
              borderTop: 'none',
              fontFamily: 'Arial, sans-serif',
              resize: 'vertical',
              lineHeight: '1.6',
              borderRadius: '0 0 4px 4px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PartDPage1;
